import { createContext, useContext, useState, useRef, useEffect, ReactNode } from 'react';
import type { MusicTrack } from '@/lib/musicTypes';

interface MusicPlayerContextType {
  currentTrack: MusicTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  playlist: MusicTrack[];
  playTrack: (track: MusicTrack, playlist?: MusicTrack[]) => void;
  togglePlay: () => void;
  pause: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
}

const MusicPlayerContext = createContext<MusicPlayerContextType | undefined>(undefined);

export function MusicPlayerProvider({ children }: { children: ReactNode }) {
  const [currentTrack, setCurrentTrack] = useState<MusicTrack | null>(null);
  const [playlist, setPlaylist] = useState<MusicTrack[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, []);

  // Global keyboard shortcut for space bar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle space bar when player is active
      if (!currentTrack) return;

      // Don't trigger if user is typing in an input, textarea, or contenteditable element
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (event.code === 'Space' && !isInputField) {
        event.preventDefault(); // Prevent page scroll
        togglePlay();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentTrack, isPlaying]); // Re-attach listener when these change

  const playTrack = (track: MusicTrack, newPlaylist?: MusicTrack[]) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Update playlist if provided
    if (newPlaylist) {
      setPlaylist(newPlaylist);
    }

    // If same track, just toggle play
    if (currentTrack?.event.id === track.event.id) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
      }
      return;
    }

    // Load new track
    setCurrentTrack(track);
    audio.src = track.audioUrl;
    audio.load();
    audio.play();
    setIsPlaying(true);
    setCurrentTime(0);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const pause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    setIsPlaying(false);
  };

  const seek = (time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
  };

  const setVolume = (newVolume: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = newVolume;
    setVolumeState(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const getCurrentTrackIndex = () => {
    if (!currentTrack || playlist.length === 0) return -1;
    return playlist.findIndex((track) => track.event.id === currentTrack.event.id);
  };

  const nextTrack = () => {
    const currentIndex = getCurrentTrackIndex();
    if (currentIndex === -1 || currentIndex >= playlist.length - 1) return;

    const nextTrack = playlist[currentIndex + 1];
    playTrack(nextTrack);
  };

  const previousTrack = () => {
    const currentIndex = getCurrentTrackIndex();
    if (currentIndex <= 0) return;

    const prevTrack = playlist[currentIndex - 1];
    playTrack(prevTrack);
  };

  const currentIndex = getCurrentTrackIndex();
  const hasNext = currentIndex !== -1 && currentIndex < playlist.length - 1;
  const hasPrevious = currentIndex > 0;

  return (
    <MusicPlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        playlist,
        playTrack,
        togglePlay,
        pause,
        seek,
        setVolume,
        toggleMute,
        nextTrack,
        previousTrack,
        hasNext,
        hasPrevious,
      }}
    >
      {children}
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer() {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  }
  return context;
}
